import {
  DataModelBuilder, SerializationContext, SerializedData,
} from '../../models/dto/data-model.js';
import { cloneMap, isSameMap, mapToData } from '../../models/dto/data-model-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';
import { VersionRange } from '../../models/core/version-range.js';
import { toEnumFromString } from '../../serialization/enum-utils.js';

export enum CollaboratorEventAccess {
  ReadOnly = 'ReadOnly',
  ReadWrite = 'ReadWrite',
  Unavailable = 'Unavailable',
}
export class CollaboratorEventAccessComponent extends Component {
  static readonly TYPE_ID: string = '779ee588c971483993cfbc078c61107a';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly eventAccessRules: Map<string, CollaboratorEventAccess>,
  ) {
    super();
  }

  get $typeId(): string {
    return CollaboratorEventAccessComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(CollaboratorEventAccessComponent.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return false;
  }

  static fromMap(
      context: SerializationContext,
      map: SerializedData
  ): CollaboratorEventAccessComponent {
    return new CollaboratorEventAccessComponent(
        MapUtils.getMap<string, CollaboratorEventAccess>(
            map,
            'eventAccessRules',
            (k, v) => {
              return { key: k as string, value: toEnumFromString(v as string, CollaboratorEventAccess) };
            },
        ),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'eventAccessRules': mapToData(this.eventAccessRules, (k, v) => {
        return { key: k, value: toEnumFromString(v, CollaboratorEventAccess) };
      }),
    };
  }

  clone(): this {
    return new CollaboratorEventAccessComponent(
        cloneMap(this.eventAccessRules),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameMap(other.eventAccessRules, this.eventAccessRules);
  }
}

export class CollaboratorEventAccessComponentBuilder extends DataModelBuilder<CollaboratorEventAccessComponent> {
  build(
      context: SerializationContext,
      map: SerializedData,
  ): CollaboratorEventAccessComponent {
    return CollaboratorEventAccessComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(CollaboratorEventAccessComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(CollaboratorEventAccessComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version enum type `CollaboratorEventAccess` was introduced, Also
    // parameter `accessibleEventIds` of type `List<String>` renamed to
    // `eventAccessRules` and converted to type
    // `Map<String, CollaboratorEventAccess>`.
    if(version < 2) {
      const accessibleEventIds = MapUtils.getList<string>(
          map,
          'accessibleEventIds',
          (x) => x as string
      );

      const eventAccessRules = new Map<string, CollaboratorEventAccess>();
      for(const id of accessibleEventIds) {
        eventAccessRules.set(id, CollaboratorEventAccess.ReadWrite);
      }

      map['eventAccessRules'] = mapToData(eventAccessRules, (k, v) => {
        return { key: k, value: toEnumFromString(v, CollaboratorEventAccess) };
      });

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
